package com.rosario.juridico.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.navigation.NavController
import com.rosario.juridico.domain.ExpedienteRepository
import com.rosario.juridico.marketplace.model.Expediente
import com.rosario.juridico.marketplace.model.Movimiento
import com.rosario.juridico.marketplace.model.Tarea
import com.rosario.juridico.marketplace.model.TiposMovimiento
import com.rosario.juridico.platform.FirebaseProxy
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime

// ── Colores ────────────────────────────────────────────────────────────────────
private val AzulPrimario  = Color(0xFF1565C0)
private val RojoUrgente   = Color(0xFFC62828)
private val AmarilloAviso = Color(0xFFF57C00)
private val VerdeOk       = Color(0xFF2E7D32)
private val GrisNeutro    = Color(0xFF546E7A)

// ── ViewModel ─────────────────────────────────────────────────────────────────
class DetalleExpedienteViewModel(
    private val repository: ExpedienteRepository = ExpedienteRepository()
) : ViewModel() {

    private val _expediente = MutableStateFlow<Expediente?>(null)
    val expediente: StateFlow<Expediente?> = _expediente.asStateFlow()

    private val _tareas = MutableStateFlow<List<Tarea>>(emptyList())
    val tareas: StateFlow<List<Tarea>> = _tareas.asStateFlow()

    private val _movimientos = MutableStateFlow<List<Movimiento>>(emptyList())
    val movimientos: StateFlow<List<Movimiento>> = _movimientos.asStateFlow()

    private val _loading = MutableStateFlow(true)
    val loading: StateFlow<Boolean> = _loading.asStateFlow()

    private val _showDialogNuevaTarea = MutableStateFlow(false)
    val showDialogNuevaTarea: StateFlow<Boolean> = _showDialogNuevaTarea.asStateFlow()

    private val _showDialogMovimiento = MutableStateFlow(false)
    val showDialogMovimiento: StateFlow<Boolean> = _showDialogMovimiento.asStateFlow()

    private val _movimientoEditar = MutableStateFlow<Movimiento?>(null)
    val movimientoEditar: StateFlow<Movimiento?> = _movimientoEditar.asStateFlow()

    private val _sugerenciaIA = MutableStateFlow<String?>(null)
    val sugerenciaIA: StateFlow<String?> = _sugerenciaIA.asStateFlow()

    private val _loadingIA = MutableStateFlow(false)
    val loadingIA: StateFlow<Boolean> = _loadingIA.asStateFlow()

    fun cargar(expedienteId: String) {
        val userId = FirebaseProxy.getCurrentUserId() ?: return
        viewModelScope.launch {
            _loading.value = true
            try {
                _tareas.value = repository.getTareas(expedienteId)
                _movimientos.value = repository.getMovimientos(userId, expedienteId)
            } catch (e: Exception) { }
            _loading.value = false
        }
    }

    fun toggleDialogoTarea(mostrar: Boolean) { _showDialogNuevaTarea.value = mostrar }

    fun abrirDialogoMovimiento(movimiento: Movimiento? = null) {
        _movimientoEditar.value = movimiento
        _showDialogMovimiento.value = true
    }

    fun cerrarDialogoMovimiento() {
        _showDialogMovimiento.value = false
        _movimientoEditar.value = null
    }

    fun agregarTarea(expedienteId: String, descripcion: String, fechaVencimiento: Long?) {
        viewModelScope.launch {
            try {
                repository.agregarTarea(
                    expedienteId,
                    Tarea(descripcion = descripcion, fechaVencimiento = fechaVencimiento)
                )
                _tareas.value = repository.getTareas(expedienteId)
            } catch (e: Exception) { }
            _showDialogNuevaTarea.value = false
        }
    }

    fun guardarMovimiento(expedienteId: String, movimiento: Movimiento) {
        val userId = FirebaseProxy.getCurrentUserId() ?: return
        viewModelScope.launch {
            try {
                if (movimiento.id.isBlank()) {
                    repository.agregarMovimiento(
                        userId, expedienteId,
                        movimiento.copy(creadoEn = Clock.System.now().toEpochMilliseconds())
                    )
                } else {
                    repository.actualizarMovimiento(userId, expedienteId, movimiento)
                }
                _movimientos.value = repository.getMovimientos(userId, expedienteId)
            } catch (e: Exception) { }
            cerrarDialogoMovimiento()
        }
    }

    fun eliminarMovimiento(expedienteId: String, movimientoId: String) {
        val userId = FirebaseProxy.getCurrentUserId() ?: return
        viewModelScope.launch {
            try {
                repository.eliminarMovimiento(userId, expedienteId, movimientoId)
                _movimientos.value = repository.getMovimientos(userId, expedienteId)
            } catch (e: Exception) { }
        }
    }

    fun sugerirProximoPaso(expediente: Expediente?, movimientos: List<Movimiento>) {
        _loadingIA.value = true
        _sugerenciaIA.value = null
        viewModelScope.launch {
            try {
                val contexto = buildString {
                    appendLine("Expediente: ${expediente?.caratula ?: "Sin carátula"}")
                    appendLine("Fuero: ${expediente?.fuero ?: "No especificado"}")
                    appendLine("Estado actual: ${expediente?.estadoProcesal ?: "No especificado"}")
                    if (movimientos.isNotEmpty()) {
                        appendLine("Ultimos movimientos:")
                        movimientos.take(5).forEach { mov ->
                            appendLine("- ${mov.tipo}: ${mov.descripcion}")
                        }
                    }
                }
                val sugerencia = generarSugerenciaIA(contexto)
                _sugerenciaIA.value = sugerencia
            } catch (e: Exception) {
                _sugerenciaIA.value = "No se pudo obtener la sugerencia. Verifica tu conexion."
            }
            _loadingIA.value = false
        }
    }

    fun limpiarSugerencia() { _sugerenciaIA.value = null }
}

// ── Sugerencia IA ─────────────────────────────────────────────────────────────
private suspend fun generarSugerenciaIA(contexto: String): String {
    return try {
        val response = com.rosario.juridico.platform.BackendClient.consultaLegal(
            prompt = "Sos un asistente juridico especializado en derecho procesal de Santa Fe, Argentina. " +
                "Analiza el siguiente expediente y sugeri el proximo paso procesal segun el CPCC Santa Fe. " +
                "Responde en maximo 3 parrafos indicando: 1) el proximo acto procesal, 2) fundamento legal, " +
                "3) plazo aproximado. IMPORTANTE: aclarar que es orientativo y no reemplaza criterio profesional.\n\n$contexto"
        )
        if (response.error != null || response.result.isBlank()) generarSugerenciaLocal(contexto)
        else response.result
    } catch (e: Exception) {
        generarSugerenciaLocal(contexto)
    }
}

private fun generarSugerenciaLocal(contexto: String): String {
    val estado = contexto.lowercase()
    return when {
        estado.contains("demanda") && !estado.contains("contestacion") ->
            "Proximo paso sugerido: Aguardar la contestacion de demanda.\n\n" +
            "El demandado tiene el plazo legal para contestar (art. 142 CPCC Santa Fe: 15 dias habiles en proceso ordinario). " +
            "Si vence el plazo sin contestacion, corresponde solicitar la declaracion de rebeldia.\n\n" +
            "Sugerencia orientativa. No reemplaza el criterio profesional del abogado."
        estado.contains("contestacion") && !estado.contains("prueba") ->
            "Proximo paso sugerido: Apertura del periodo probatorio.\n\n" +
            "Una vez contestada la demanda, corresponde ofrecer prueba dentro del plazo legal (art. 155 CPCC Santa Fe). " +
            "Preparar el escrito de ofrecimiento de prueba documental, testimonial y/o pericial.\n\n" +
            "Sugerencia orientativa. No reemplaza el criterio profesional del abogado."
        estado.contains("prueba") && !estado.contains("alegato") ->
            "Proximo paso sugerido: Presentacion de alegatos.\n\n" +
            "Concluido el periodo de prueba, corresponde presentar el alegato de bien probado (art. 199 CPCC Santa Fe). " +
            "Analizar la prueba producida y elaborar el alegato destacando los hechos favorables.\n\n" +
            "Sugerencia orientativa. No reemplaza el criterio profesional del abogado."
        estado.contains("sentencia") ->
            "Proximo paso sugerido: Evaluar recurso de apelacion.\n\n" +
            "Si la sentencia es desfavorable, el recurso de apelacion debe interponerse dentro de los 5 dias habiles " +
            "(art. 346 CPCC Santa Fe). Verificar los agravios y fundamentar el recurso.\n\n" +
            "Sugerencia orientativa. No reemplaza el criterio profesional del abogado."
        estado.contains("mediacion") ->
            "Proximo paso sugerido: Continuar o concluir la mediacion prejudicial.\n\n" +
            "En Santa Fe la mediacion prejudicial es obligatoria en materias civiles y comerciales (Ley 13.151). " +
            "Si fracasa, labrar el acta de cierre y proceder a la interposicion de la demanda judicial.\n\n" +
            "Sugerencia orientativa. No reemplaza el criterio profesional del abogado."
        else ->
            "Proximo paso sugerido: Revisar el estado procesal actual.\n\n" +
            "Para una sugerencia mas precisa, actualiza el estado procesal del expediente y registra los ultimos movimientos. " +
            "Consulta el CPCC de Santa Fe (Ley 5531) y la practica del juzgado interviniente.\n\n" +
            "Sugerencia orientativa. No reemplaza el criterio profesional del abogado."
    }
}
